import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { assetAPI, customerAPI, exportAPI } from "../lib/api";
import { formatDate } from "../lib/utils";
import { Plus, Package, User, Calendar, Hash, Edit2, Trash2, MapPin, Monitor, Download, Wifi } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const ASSET_TYPES = [
  { value: "computer", label: "Computer" },
  { value: "printer", label: "Printer" },
  { value: "color_printer", label: "Color Printer" },
  { value: "scanner", label: "Scanner" },
  { value: "router", label: "Router" },
  { value: "access_point", label: "Access Point" },
  { value: "nas", label: "NAS" },
  { value: "switch", label: "Switch" },
  { value: "networking_device", label: "Networking Device" },
  { value: "software", label: "Software" }
];

const REMOTE_ACCESS_TYPES = [
  { value: "teamviewer", label: "TeamViewer" },
  { value: "splashtop", label: "Splashtop" },
  { value: "other", label: "Other" }
];

export function Assets() {
  const { canEditAssets, user, isAdmin } = useAuth();
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [sortBy, setSortBy] = useState("name_asc"); // Default sort assets within groups by name
  const [selectedCustomer, setSelectedCustomer] = useState("all"); // Customer filter
  const [newAsset, setNewAsset] = useState({
    customer_id: "",
    name: "",
    serial_number: "",
    purchase_date: "",
    details: "",
    location: "",
    user: "",
    asset_type: "",
    remote_access: "",
    remote_access_password: "",
    remote_access_type: "teamviewer"
  });
  const [editAsset, setEditAsset] = useState({
    customer_id: "",
    name: "",
    serial_number: "",
    purchase_date: "",
    details: "",
    location: "",
    user: "",
    asset_type: "",
    remote_access: "",
    remote_access_password: "",
    remote_access_type: "teamviewer"
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [assetsRes, customersRes] = await Promise.all([
        assetAPI.getAll(),
        customerAPI.getAll()
      ]);
      setAssets(assetsRes.data);
      // Sort customers alphabetically by name for dropdown
      const sortedCustomers = customersRes.data.sort((a, b) => a.name.localeCompare(b.name));
      setCustomers(sortedCustomers);
    } catch (error) {
      toast.error("Failed to load data");
      console.error("Load data error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    if (!newAsset.customer_id || !newAsset.name.trim()) {
      toast.error("Customer and asset name are required");
      return;
    }

    try {
      const assetData = {
        ...newAsset,
        purchase_date: newAsset.purchase_date || null
      };
      
      await assetAPI.create(assetData);
      toast.success("Asset registered successfully");
      setShowCreateDialog(false);
      setNewAsset({
        customer_id: "",
        name: "",
        serial_number: "",
        purchase_date: "",
        details: "",
        location: "",
        user: "",
        asset_type: ""
      });
      loadData();
    } catch (error) {
      toast.error("Failed to register asset");
      console.error("Create asset error:", error);
    }
  };

  const handleEditAsset = (asset) => {
    setEditingAsset(asset);
    setEditAsset({
      customer_id: asset.customer_id,
      name: asset.name,
      serial_number: asset.serial_number || "",
      purchase_date: asset.purchase_date || "",
      details: asset.details || "",
      location: asset.location || "",
      user: asset.user || "",
      asset_type: asset.asset_type || "",
      remote_access: asset.remote_access || "",
      remote_access_password: asset.remote_access_password || "",
      remote_access_type: asset.remote_access_type || "teamviewer"
    });
    setShowEditDialog(true);
  };

  const handleUpdateAsset = async (e) => {
    e.preventDefault();
    if (!editAsset.customer_id || !editAsset.name.trim()) {
      toast.error("Customer and asset name are required");
      return;
    }

    try {
      const assetData = {
        ...editAsset,
        purchase_date: editAsset.purchase_date || null
      };
      
      await assetAPI.update(editingAsset.id, assetData);
      toast.success("Asset updated successfully");
      setShowEditDialog(false);
      setEditingAsset(null);
      loadData();
    } catch (error) {
      toast.error("Failed to update asset");
      console.error("Update asset error:", error);
    }
  };

  const handleDeleteAsset = async (asset) => {
    if (!window.confirm(`Are you sure you want to delete the asset "${asset.name}" for ${getCustomerName(asset.customer_id)}? This action cannot be undone.`)) {
      return;
    }

    try {
      await assetAPI.delete(asset.id);
      toast.success("Asset deleted successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to delete asset");
      console.error("Delete asset error:", error);
    }
  };

  const handleExportCustomerAssetsPDF = async (customerId, customerName) => {
    try {
      const response = await assetAPI.exportCustomerAssetsPDF(customerId);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${customerName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_assets.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`PDF exported for ${customerName}`);
    } catch (error) {
      toast.error("Failed to export PDF");
      console.error("PDF export error:", error);
    }
  };

  const handleExportAssets = async () => {
    try {
      const response = await exportAPI.exportAssetsCSV();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'assets_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Assets exported successfully");
    } catch (error) {
      toast.error("Failed to export assets");
      console.error("Export assets error:", error);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : "Unknown Customer";
  };

  const getAssetTypeLabel = (assetType) => {
    const type = ASSET_TYPES.find(t => t.value === assetType);
    return type ? type.label : assetType;
  };

  const getGroupedAssetsByCustomer = () => {
    // Filter assets by selected customer if any
    const filteredAssets = selectedCustomer && selectedCustomer !== "all"
      ? assets.filter(asset => asset.customer_id === selectedCustomer)
      : assets;
    
    // Group assets by customer
    const grouped = {};
    
    filteredAssets.forEach(asset => {
      const customerId = asset.customer_id;
      const customerName = getCustomerName(customerId);
      
      if (!grouped[customerId]) {
        grouped[customerId] = {
          customerName: customerName,
          assets: []
        };
      }
      
      grouped[customerId].assets.push(asset);
    });
    
    // Sort customers alphabetically and sort assets within each group
    return Object.values(grouped).sort((a, b) => a.customerName.localeCompare(b.customerName))
      .map(group => ({
        ...group,
        assets: group.assets.sort((a, b) => {
          switch (sortBy) {
            case "name_asc":
              return a.name.localeCompare(b.name);
            case "name_desc":
              return b.name.localeCompare(a.name);
            case "date_desc":
              return new Date(b.purchase_date || 0) - new Date(a.purchase_date || 0);
            case "date_asc":
              return new Date(a.purchase_date || 0) - new Date(b.purchase_date || 0);
            case "type_asc":
              return getAssetTypeLabel(a.asset_type || "").localeCompare(getAssetTypeLabel(b.asset_type || ""));
            case "type_desc":
              return getAssetTypeLabel(b.asset_type || "").localeCompare(getAssetTypeLabel(a.asset_type || ""));
            default:
              return a.name.localeCompare(b.name);
          }
        })
      }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="assets-title">
            Assets
          </h1>
          <p className="text-slate-600 mt-1">Manage customer assets and equipment</p>
        </div>
        
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={handleExportAssets}
              className="btn-hover"
              data-testid="export-assets-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          
          {canEditAssets && (
            <div>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="btn-hover" data-testid="add-asset-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Register Asset
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Register New Asset</DialogTitle>
                <DialogDescription>
                  Add a new asset to a customer's inventory.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateAsset} className="space-y-4" data-testid="create-asset-form">
                <div>
                  <Label htmlFor="customer">Customer *</Label>
                  <Select
                    value={newAsset.customer_id}
                    onValueChange={(value) => setNewAsset({ ...newAsset, customer_id: value })}
                  >
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="name">Asset Name *</Label>
                  <Input
                    id="name"
                    value={newAsset.name}
                    onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                    placeholder="e.g. MacBook Pro, Server, Printer"
                    required
                    data-testid="asset-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="serial_number">Serial Number</Label>
                    <Input
                      id="serial_number"
                      value={newAsset.serial_number}
                      onChange={(e) => setNewAsset({ ...newAsset, serial_number: e.target.value })}
                      placeholder="e.g. SN123456789"
                      data-testid="asset-serial-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchase_date">Purchase Date</Label>
                    <Input
                      id="purchase_date"
                      type="date"
                      value={newAsset.purchase_date}
                      onChange={(e) => setNewAsset({ ...newAsset, purchase_date: e.target.value })}
                      data-testid="asset-purchase-date-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="asset_type">Asset Type</Label>
                  <Select value={newAsset.asset_type} onValueChange={(value) => setNewAsset({ ...newAsset, asset_type: value })}>
                    <SelectTrigger data-testid="asset-type-select">
                      <SelectValue placeholder="Select asset type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Remote Access Fields */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="remote_access_type">Remote Access Type</Label>
                    <Select value={newAsset.remote_access_type} onValueChange={(value) => setNewAsset({ ...newAsset, remote_access_type: value })}>
                      <SelectTrigger data-testid="remote-access-type-select">
                        <SelectValue placeholder="Select remote access type" />
                      </SelectTrigger>
                      <SelectContent>
                        {REMOTE_ACCESS_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="remote_access">Remote Access ID</Label>
                    <Input
                      id="remote_access"
                      value={newAsset.remote_access}
                      onChange={(e) => setNewAsset({ ...newAsset, remote_access: e.target.value })}
                      placeholder="e.g. ID, URL, or connection info"
                      data-testid="remote-access-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="remote_access_password">Remote Access Password</Label>
                    <Input
                      id="remote_access_password"
                      type="text"
                      value={newAsset.remote_access_password}
                      onChange={(e) => setNewAsset({ ...newAsset, remote_access_password: e.target.value })}
                      placeholder="Enter password"
                      data-testid="remote-access-password-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={newAsset.location}
                      onChange={(e) => setNewAsset({ ...newAsset, location: e.target.value })}
                      placeholder="e.g. Office 1A, Server Room"
                      data-testid="asset-location-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="user">Assigned User</Label>
                    <Input
                      id="user"
                      value={newAsset.user}
                      onChange={(e) => setNewAsset({ ...newAsset, user: e.target.value })}
                      placeholder="e.g. John Doe"
                      data-testid="asset-user-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="details">Details</Label>
                  <Textarea
                    id="details"
                    value={newAsset.details}
                    onChange={(e) => setNewAsset({ ...newAsset, details: e.target.value })}
                    placeholder="Additional details about the asset..."
                    rows={3}
                    data-testid="asset-details-input"
                  />
                </div>
                
                <div className="flex space-x-2">
                  <Button type="submit" className="flex-1" data-testid="save-asset-button">
                    Register Asset
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                    data-testid="cancel-asset-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Asset Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Asset</DialogTitle>
              <DialogDescription>
                Update the asset information
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateAsset} className="space-y-4">
              <div>
                <Label htmlFor="edit-customer">Customer *</Label>
                <Select value={editAsset.customer_id} onValueChange={(value) => setEditAsset({ ...editAsset, customer_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-name">Asset Name *</Label>
                <Input
                  id="edit-name"
                  value={editAsset.name}
                  onChange={(e) => setEditAsset({ ...editAsset, name: e.target.value })}
                  placeholder="e.g. MacBook Pro, Server, Printer"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-serial_number">Serial Number</Label>
                  <Input
                    id="edit-serial_number"
                    value={editAsset.serial_number}
                    onChange={(e) => setEditAsset({ ...editAsset, serial_number: e.target.value })}
                    placeholder="e.g. SN123456789"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-purchase_date">Purchase Date</Label>
                  <Input
                    id="edit-purchase_date"
                    type="date"
                    value={editAsset.purchase_date}
                    onChange={(e) => setEditAsset({ ...editAsset, purchase_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-asset_type">Asset Type</Label>
                <Select value={editAsset.asset_type} onValueChange={(value) => setEditAsset({ ...editAsset, asset_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select asset type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Remote Access Fields */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="edit-remote_access_type">Remote Access Type</Label>
                  <Select value={editAsset.remote_access_type} onValueChange={(value) => setEditAsset({ ...editAsset, remote_access_type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select remote access type" />
                    </SelectTrigger>
                    <SelectContent>
                      {REMOTE_ACCESS_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-remote_access">Remote Access ID</Label>
                  <Input
                    id="edit-remote_access"
                    value={editAsset.remote_access}
                    onChange={(e) => setEditAsset({ ...editAsset, remote_access: e.target.value })}
                    placeholder="e.g. ID, URL, or connection info"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-remote_access_password">Remote Access Password</Label>
                  <Input
                    id="edit-remote_access_password"
                    type="text"
                    value={editAsset.remote_access_password}
                    onChange={(e) => setEditAsset({ ...editAsset, remote_access_password: e.target.value })}
                    placeholder="Enter password"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-location">Location</Label>
                  <Input
                    id="edit-location"
                    value={editAsset.location}
                    onChange={(e) => setEditAsset({ ...editAsset, location: e.target.value })}
                    placeholder="e.g. Office 1A, Server Room"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-user">Assigned User</Label>
                  <Input
                    id="edit-user"
                    value={editAsset.user}
                    onChange={(e) => setEditAsset({ ...editAsset, user: e.target.value })}
                    placeholder="e.g. John Doe"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-details">Details</Label>
                <Textarea
                  id="edit-details"
                  value={editAsset.details}
                  onChange={(e) => setEditAsset({ ...editAsset, details: e.target.value })}
                  placeholder="Additional details about the asset..."
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="submit" data-testid="update-asset-btn">
                  Update Asset
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>

      {/* Filter and Sorting Controls */}
      {assets.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Label htmlFor="customer-filter">Filter by Customer:</Label>
                <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                  <SelectTrigger className="w-48" data-testid="customer-filter-select">
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Label htmlFor="sort-by">Sort assets within groups by:</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-48" data-testid="assets-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name_asc">Asset Name (A-Z)</SelectItem>
                    <SelectItem value="name_desc">Asset Name (Z-A)</SelectItem>
                    <SelectItem value="type_asc">Device Type (A-Z)</SelectItem>
                    <SelectItem value="type_desc">Device Type (Z-A)</SelectItem>
                    <SelectItem value="date_desc">Purchase Date (Newest)</SelectItem>
                    <SelectItem value="date_asc">Purchase Date (Oldest)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="text-sm text-slate-600">
              {getGroupedAssetsByCustomer().reduce((total, group) => total + group.assets.length, 0)} asset{getGroupedAssetsByCustomer().reduce((total, group) => total + group.assets.length, 0) !== 1 ? 's' : ''} 
              {selectedCustomer && selectedCustomer !== "all" && ` for ${getCustomerName(selectedCustomer)}`}
            </div>
          </div>
        </div>
      )}

      {/* Assets Grouped by Customer */}
      {assets.length > 0 ? (
        <div className="space-y-8" data-testid="assets-by-customer">
          {getGroupedAssetsByCustomer().map((customerGroup) => (
            <div key={customerGroup.customerName} className="space-y-4">
              {/* Customer Header */}
              <div className="flex items-center justify-between pb-3 border-b-2 border-slate-200">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      {customerGroup.customerName}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {customerGroup.assets.length} asset{customerGroup.assets.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const customerId = customerGroup.assets[0]?.customer_id;
                    if (customerId) {
                      handleExportCustomerAssetsPDF(customerId, customerGroup.customerName);
                    }
                  }}
                  className="text-green-600 hover:text-green-800 hover:bg-green-50"
                  title={`Export ${customerGroup.customerName} assets as PDF`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
              
              {/* Assets Grid for this Customer */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-6">
                {customerGroup.assets.map((asset) => (
                  <Card key={asset.id} className="card-hover" data-testid={`asset-card-${asset.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg truncate">{asset.name}</CardTitle>
                          {asset.asset_type && (
                            <CardDescription className="truncate">
                              {getAssetTypeLabel(asset.asset_type)}
                            </CardDescription>
                          )}
                        </div>
                        {canEditAssets && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditAsset(asset)}
                              data-testid={`edit-asset-${asset.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteAsset(asset)}
                              data-testid={`delete-asset-${asset.id}`}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {asset.serial_number && (
                          <div className="flex items-center text-sm text-slate-600">
                            <Hash className="w-4 h-4 mr-2 text-slate-400" />
                            <span className="font-mono">{asset.serial_number}</span>
                          </div>
                        )}
                        
                        {asset.purchase_date && (
                          <div className="flex items-center text-sm text-slate-600">
                            <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                            <span>Purchased: {formatDate(asset.purchase_date)}</span>
                          </div>
                        )}

                        {asset.location && (
                          <div className="flex items-center text-sm text-slate-600">
                            <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                            <span>Location: {asset.location}</span>
                          </div>
                        )}

                        {asset.user && (
                          <div className="flex items-center text-sm text-slate-600">
                            <User className="w-4 h-4 mr-2 text-slate-400" />
                            <span>Assigned to: {asset.user}</span>
                          </div>
                        )}

                        {asset.remote_access && (
                          <div className="flex items-center text-sm text-slate-600">
                            <Wifi className="w-4 h-4 mr-2 text-slate-400" />
                            <span>Remote: {asset.remote_access_type} - {asset.remote_access}</span>
                          </div>
                        )}
                        
                        {asset.details && (
                          <div className="text-sm text-slate-700 bg-slate-50 p-2 rounded-md">
                            {asset.details}
                          </div>
                        )}
                        
                        <div className="text-xs text-slate-500 pt-2 border-t">
                          Registered: {formatDate(asset.created_date)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-16">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2" data-testid="no-assets-title">
              No assets registered
            </h3>
            <p className="text-slate-600 mb-6">
              Start tracking customer assets and equipment inventory
            </p>
            {canEditAssets && (
              <Button onClick={() => setShowCreateDialog(true)} data-testid="add-first-asset-button">
                <Plus className="w-4 h-4 mr-2" />
                Register Your First Asset
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
